import React, { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { jobBoardApi } from '../../api/jobBoardApi';
import { JobBoardStats, ApplicationStatus } from '../../types/jobBoard';
import Spinner from '../../components/Spinner';

const COLORS: Record<string, string> = {
  Applied: '#94a3b8',
  UnderReview: '#6366f1',
  InterviewScheduled: '#8b5cf6',
  Offered: '#ec4899',
  Rejected: '#ef4444',
  Hired: '#10b981',
};

const ApplicantsOverview = () => {
  const [stats, setStats] = useState<JobBoardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await jobBoardApi.getOverviewStats();
        setStats(res.data);
      } catch (err) {
        console.error("Error fetching applicant stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statusKeys: ApplicationStatus[] = ['Applied', 'UnderReview', 'InterviewScheduled', 'Offered', 'Rejected', 'Hired'];
  const chartData = statusKeys.map(key => ({
    status: key.replace(/([A-Z])/g, ' $1').trim(),
    name: stats?.applicantsByStatus?.[key] || 0,
    color: COLORS[key] || '#cbd5e1'
  }));

  if (loading) return <div className="p-8 flex justify-center"><Spinner /></div>;

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      <header className="mt-5 mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <Users className="text-indigo-600" size={24} />
            Applicants Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Track candidate pipelines and application statuses.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-sm border border-slate-100 min-w-0">
          <h3 className="text-lg font-bold mb-6 text-slate-800">Status Distribution</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData.length > 0 ? chartData : [{ status: 'None', name: 1, color: '#f1f5f9' }]} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="name" nameKey="status">
                  {chartData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                {/* <Legend iconType="circle" /> */}
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {chartData.map(item => (
              <div key={item.status} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: item.color }}></div>
                <span className="text-sm font-medium text-slate-600">{item.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <h3 className="text-lg font-bold mb-6 text-slate-800">Hiring Velocity</h3>
          <div className="h-64 flex items-center justify-center text-slate-400">
            <p>Hiring cycle data visualization will appear here.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ApplicantsOverview;